import React, { useCallback, useState } from 'react';
import { useAppDispatch } from 'modules/main';

import { RegularBase } from 'modules/ui/typography';
import { Tooltip, Spinner } from 'modules/ui/components';
import { DownloadIcon, TrashBinIcon } from 'modules/ui/assets';
import { RouteV2 } from '../../types';
import {
  downloadServerRoutingV2FilesThunk,
  deleteServerRouteV2Thunk,
} from '../../redux/thunk';
import { formatRoutingV2Subtitle } from '../../utils/formater';

import {
  Container,
  InfoContainer,
  Subtitle,
  ProcessingLabel,
  ErrorLabel,
  StatusContainer,
} from './styledComponents';

type Props = {
  route: RouteV2;
};

const isDone = (state: string) => state.toLowerCase() === 'ready';
const isProcessing = (state: string) =>
  state.toLowerCase() === 'running' ||
  state.toLowerCase() === 'pending' ||
  state.toLowerCase() === 'staging';
const isError = (state: string) => state.toLowerCase() === 'failed';

const RowV2: React.FC<Props> = ({ route }) => {
  const dispatch = useAppDispatch();
  const [isHovering, setIsHovering] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const onMouseEnter = useCallback(() => setIsHovering(true), []);
  const onMouseExit = useCallback(() => setIsHovering(false), []);

  const onFinish = useCallback(() => setIsDownloading(false), []);

  const downloadRoute = useCallback(() => {
    setIsDownloading(true);
    dispatch(downloadServerRoutingV2FilesThunk(route, onFinish));
  }, [route, dispatch, onFinish]);

  const deleteRoute = useCallback(() => {
    if (
      // eslint-disable-next-line no-alert
      window.confirm(
        `Are you sure you want to delete the ${route.name} from the server?`,
      )
    ) {
      dispatch(deleteServerRouteV2Thunk(route));
    }
  }, [route, dispatch]);

  return (
    <Container onMouseEnter={onMouseEnter} onMouseLeave={onMouseExit}>
      <InfoContainer>
        <RegularBase>{route.name}</RegularBase>
        <Subtitle>
          {formatRoutingV2Subtitle(
            route.model,
            route.grib_type,
            route.polarSpread,
            route.start_time,
          )}
        </Subtitle>
      </InfoContainer>
      {isDownloading && (
        <StatusContainer>
          <Spinner />
        </StatusContainer>
      )}
      {!isDownloading && (
        <StatusContainer>
          {isHovering && <TrashBinIcon onClick={deleteRoute} />}
          {isError(route.state) && !!route.last_error && (
            <Tooltip place="left" text={route.last_error} multiline />
          )}
          {isDone(route.state) && <DownloadIcon onClick={downloadRoute} />}
          {isProcessing(route.state) && (
            <ProcessingLabel>Processing... </ProcessingLabel>
          )}
          {isError(route.state) && <ErrorLabel>Failed</ErrorLabel>}
        </StatusContainer>
      )}
    </Container>
  );
};

export default React.memo(RowV2);
